import React, { useState, useEffect } from 'react';
import { format, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog '; // ¡Importa el nuevo componente!



// MUI Components
import {
  Modal,
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  TextField,
  FormLabel,
  Grid,
  Paper,
  MenuItem, // Import MenuItem for Select
  Select,   // Import Select for dropdown
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

// Modal style
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 600 }, // Responsive width
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
};

function BookingModal({ selectedDate, dayData, onClose, onReservaSuccess, opcionesAdicionales, tiposReservaBase }) {
  const { isAdmin } = useAuth();
  const [selectedTurno, setSelectedTurno] = useState('');
  const [selectedTipoReserva, setSelectedTipoReserva] = useState(''); // New state for tipoReserva    
  const [selectedOpciones, setSelectedOpciones] = useState({});
  const [precioTotal, setPrecioTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conceptoBloqueo, setConceptoBloqueo] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const dayOfWeek = getDay(selectedDate); // 0 = domingo, 6 = sábado
  const [openConfirm, setOpenConfirm] = useState(false);


  // Definimos el horario nocturno según el día
  const horarioNoche = (dayOfWeek === 5 || dayOfWeek === 6) ? '19:00 - 04:00' : '19:00 - 00:00';


  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  useEffect(() => {
    const calcularPrecio = () => {
      let basePrice = 0;

      // 1. Encontrar el objeto de tipo de reserva seleccionado
      // Asumiendo que `selectedTipoReserva` ahora almacena el ID del tipo de reserva
      const tipoReservaSeleccionado = tiposReservaBase.find(
        (tipo) => tipo.id === parseInt(selectedTipoReserva)
      );

      // 2. Si se encuentra, usar su precio
      if (tipoReservaSeleccionado) {
        basePrice = tipoReservaSeleccionado.precio;
      }

      let opcionesPrice = 0;
      for (const id in selectedOpciones) {
        const cantidad = selectedOpciones[id];
        const opcion = opcionesAdicionales.find(o => o.id === parseInt(id));
        if (opcion && cantidad > 0) {
          opcionesPrice += opcion.precio * cantidad;
        }
      }
      setPrecioTotal(basePrice + opcionesPrice);
    };

    calcularPrecio();
    // Asegúrate de que `tiposReservaBase` sea una dependencia
  }, [selectedTurno, selectedOpciones, selectedTipoReserva, opcionesAdicionales, tiposReservaBase]);

  const handleOpcionChange = (opcionId, tipo) => {
    if (tipo === 'Leña') {
      setSelectedOpciones(prev => ({
        ...prev,
        [opcionId]: (prev[opcionId] || 0) + 1
      }));
    } else {
      setSelectedOpciones(prev => ({
        ...prev,
        [opcionId]: prev[opcionId] ? 0 : 1
      }));
    }
  };

  const detalleMedioDia = dayData?.detallesReservas?.find(r => r.turno === 'MedioDia') || null;
  const detalleNoche = dayData?.detallesReservas?.find(r => r.turno === 'Noche') || null;

  const buildInfo = (detalle) => {
    if (!detalle) return { disabled: false, text: 'Disponible' };

    if (detalle.esBloqueoAdmin) {
      return { disabled: true, text: `Bloqueado${detalle.concepto ? `: ${detalle.concepto}` : ''}` };
    }

    return { disabled: true, text: `Reservado: Apto ${detalle.numeroApartamento}` };
  };

  const infoMedioDia = buildInfo(detalleMedioDia);
  const infoNoche = buildInfo(detalleNoche);

  // Para usuario normal: no clickeable si está ocupado/bloqueado.
  // Para admin: dejalo clickeable (porque puede bloquear/desbloquear/gestionar).
  const disabledMedioDia = !isAdmin && infoMedioDia.disabled;
  const disabledNoche = !isAdmin && infoNoche.disabled;


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!selectedTurno) {
      setError('Por favor, selecciona un turno.');
      setLoading(false);
      return;
    }

    if (!selectedTipoReserva && !isAdmin) { // Validate new field
      setError('Por favor, selecciona un tipo de reserva.');
      setLoading(false);
      return;
    }

    const reservaData = {
      fechaReserva: selectedDate.toISOString(),
      turno: selectedTurno === 'MedioDia' ? 0 : 1,
      tipoReservaBase: selectedTipoReserva,
      opcionesSeleccionadas: Object.keys(selectedOpciones)
        .filter(id => selectedOpciones[id] > 0)
        .map(id => ({
          opcionAdicionalId: parseInt(id),
          cantidad: selectedOpciones[id]
        }))
    };



    if (!isAdmin) {
      try {
        await api.post('/Reservas', reservaData);
        onReservaSuccess();
        onClose();
        enqueueSnackbar('¡Reserva realizada con éxito!', { variant: 'success' });
      } catch (err) {
        console.error('Error al crear reserva:', err.response?.data || err);
        setError(err.response?.data || 'Error al crear la reserva.');
        enqueueSnackbar('Error al crear la reserva.', { variant: 'error' });
        setLoading(false);
      }
    } else {
      try {
        const reservaDataAdmin = {
          FechaBloqueo: selectedDate.toISOString(),
          turno: selectedTurno === 'MedioDia' ? 0 : 1,
          concepto: conceptoBloqueo
        }

        await api.post('/Reservas/admin/bloquear', reservaDataAdmin);
        onReservaSuccess();
        onClose();
        enqueueSnackbar('Turno bloqueado con éxito!', { variant: 'success' });
      } catch (err) {
        console.error('Error al bloquear el turno: ', err.response?.data || err);
        setError(err.response?.data || 'Error al bloquear el turno');
        enqueueSnackbar('Error al bloquear la fecha.', { variant: 'error' });
        setLoading(false);
      }
    }

  };


  const handleDesbloquear = async () => {


    if (!selectedTurno) {
      setError('Por favor, selecciona un turno.');
      setLoading(false);
      return;
    }
    try {
      const desbloqueoDataAdmin = {
        fechaBloqueo: selectedDate.toISOString(),
        turno: selectedTurno === 'MedioDia' ? 0 : 1,
      }
      console.log(desbloqueoDataAdmin);

      await api.post('/Reservas/admin/desbloquear', desbloqueoDataAdmin);
      onReservaSuccess();
      onClose();
      enqueueSnackbar('Turno desbloqueada con éxito!', { variant: 'success' });
    } catch (err) {
      console.error('Error al desbloquear la fecha: ', err.response?.data || err);
      setError(err.response?.data || 'Error al bloquear la fecha');
      enqueueSnackbar('Error al desbloquear la fecha.', { variant: 'error' });
      setLoading(false);
    }
    handleCloseConfirm();

  };


  if (!isAdmin) {
    return (
      <Modal
        open={Boolean(selectedDate)}
        onClose={onClose}
        aria-labelledby="booking-modal-title"
        aria-describedby="booking-modal-description"
      >
        <Box sx={style} component="form" onSubmit={handleSubmit}>
          <Typography id="booking-modal-title" variant="h5" component="h2" gutterBottom>
            Reservar para el {format(selectedDate, 'PPP', { locale: es })}
          </Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel id="tipo-reserva-label">Tipo de Reserva</InputLabel>
            <Select
              labelId="tipo-reserva-label"
              id="tipo-reserva-select"
              value={selectedTipoReserva}
              label="Tipo de Reserva"
              onChange={(e) => setSelectedTipoReserva(e.target.value)}
            >
              <MenuItem value="">
                <em>Ninguno</em>
              </MenuItem>
              {tiposReservaBase.map((tipo) => (
                <MenuItem key={tipo.id} value={tipo.id}>
                  {tipo.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormLabel component="legend">Turno:</FormLabel>
            <RadioGroup
              row
              name="turno"
              value={selectedTurno}
              onChange={(e) => setSelectedTurno(e.target.value)}
            >
              <FormControlLabel
                value="MedioDia"
                disabled={disabledMedioDia}
                control={<Radio />}
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      Medio Día (09:00 - 16:00)
                    </Typography>
                    <Typography variant="caption" color={disabledMedioDia ? "error.main" : "success.main"}>
                      {infoMedioDia.text}
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                value="Noche"
                disabled={disabledNoche}
                control={<Radio />}
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      Noche ({horarioNoche})
                    </Typography>
                    <Typography variant="caption" color={disabledNoche ? "error.main" : "success.main"}>
                      {infoNoche.text}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>

          </FormControl>

          <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 2 }}>
            Opciones Adicionales:
          </Typography>
          <Grid container spacing={2}>
            {opcionesAdicionales
              .filter(opcion => opcion.habilitada) // <-- Filtramos por las opciones habilitadas
              .map(opcion => (<Grid item xs={12} sm={6} key={opcion.id}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1">{opcion.nombre}  </Typography>
                    <Typography variant="body2" color="text.secondary"> :  ${opcion.precio.toFixed(2)}</Typography>
                  </Box>
                  {opcion.tipo === 'Leña' ? (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <IconButton
                        aria-label="restar leña"
                        onClick={() => setSelectedOpciones(prev => ({ ...prev, [opcion.id]: Math.max(0, (prev[opcion.id] || 0) - 1) }))}
                        size="small"
                        color="error"
                      >
                        <RemoveIcon />
                      </IconButton>
                      <Typography>{selectedOpciones[opcion.id] || 0}</Typography>
                      <IconButton
                        aria-label="sumar leña"
                        onClick={() => setSelectedOpciones(prev => ({ ...prev, [opcion.id]: (prev[opcion.id] || 0) + 1 }))}
                        size="small"
                        color="success"
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedOpciones[opcion.id] === 1}
                          onChange={() => handleOpcionChange(opcion.id, opcion.tipo)}
                        />
                      }
                      label="Incluir"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Paper>
              </Grid>
              ))}
          </Grid>

          <Typography variant="h5" component="p" align="right" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
            Precio Total: ${precioTotal.toFixed(2)}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ mr: 1 }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              endIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Reservando...' : 'Confirmar Reserva'}
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  } else
    return (
      <Modal
        open={Boolean(selectedDate)}
        onClose={onClose}
        aria-labelledby="booking-modal-title"
        aria-describedby="booking-modal-description"
      >
        <Box sx={style} component="form" onSubmit={handleSubmit}>
          <Typography id="booking-modal-title" variant="h5" component="h2" gutterBottom>
            Gestión de turnos para el {format(selectedDate, 'PPP', { locale: es })}
          </Typography>

          <TextField
            fullWidth
            label="Concepto del bloqueo (opcional)"
            value={conceptoBloqueo}
            onChange={(e) => setConceptoBloqueo(e.target.value)}
            variant="outlined"
            margin="normal"
            sx={{ mb: 3 }}
          />
          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormLabel component="legend">Turno:</FormLabel>
            <RadioGroup
              row
              name="turno"
              value={selectedTurno}
              onChange={(e) => setSelectedTurno(e.target.value)}
            >
              <FormControlLabel
                value="MedioDia"
                disabled={disabledMedioDia}
                control={<Radio />}
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      Medio Día (09:00 - 16:00)
                    </Typography>
                    <Typography variant="caption" color={disabledMedioDia ? "error.main" : "success.main"}>
                      {infoMedioDia.text}
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                value="Noche"
                disabled={disabledNoche}
                control={<Radio />}
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      Noche ({horarioNoche})
                    </Typography>
                    <Typography variant="caption" color={disabledNoche ? "error.main" : "success.main"}>
                      {infoNoche.text}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>

          </FormControl>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ mr: 1 }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              endIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Bloqueando...' : 'Bloquear turno'}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleOpenConfirm} // <-- Llama a la función para desbloquear
              disabled={loading}
            >
              Desbloquear turno
            </Button>
          </Box>
          <ConfirmDeleteDialog
            open={openConfirm}
            title="Eliminar Reserva"
            onClose={handleCloseConfirm}
            onConfirm={handleDesbloquear}
            message={`¿Estás seguro de que quieres eliminar la reserva del día "${format(selectedDate, 'PPP', { locale: es })}" para el turno "${selectedTurno}"? Esta acción es irreversible.`} />
        </Box>
      </Modal>

    );
}


export default BookingModal;